import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {Selected, pricingSliderActioned} from '@/analytics/segment'
import {pricingBillingCycleActioned, PricingBillingCycleActionedAction} from '@/analytics/segment'
import OptionsToggle from '@/components/options-toggle'
import {BillingCycle, PricingV2} from '@/components/pricing'
import RichText, {hasRichText} from '@/components/rich-text'
import Slider from '@/components/slider'
import {Text} from '@/components/text'
import {useDebounce} from '@/hooks/use-debounce'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import mq from '@/utils/media-query'

import type {Props as RichTextProps} from '@/components/rich-text'
import type {PricingSectionQuery} from '@/graphql-types'

interface Props {
    title?: RichTextProps['content']
    withTitleImage?: boolean
    dataTestId?: string
    rawDemoPath?: string
    secondaryLocale?: string
}

interface PricingTiersProps {
    content: PricingSectionQuery['allPrismicPricingTiers']['nodes'][0]
}

export const PricingSection = (props: Props) => {
    const {allPrismicPricingTiers, allPrismicSecondaryPricingTiers} =
        useStaticQuery<PricingSectionQuery>(query)
    const [content] = useLocalizedContent(allPrismicPricingTiers.nodes)

    return (
        <PricingTiers
            content={
                allPrismicSecondaryPricingTiers.nodes.find(
                    (node) => node.data.locale_code === props.secondaryLocale,
                ) || content
            }
            {...props}
        />
    )
}

export const PricingTiers = ({content, rawDemoPath, dataTestId}: PricingTiersProps & Props) => {
    const [billingCycle, setBillingCycle] = React.useState<BillingCycle>(BillingCycle.Yearly)
    const [numOfUsers, setNumOfUsers] = React.useState(2)
    const [hasMounted, setHasMounted] = React.useState(false)
    const debouncedNumOfUser = useDebounce(numOfUsers, 500)

    const handleToggleChange = (cycle: BillingCycle) => {
        pricingBillingCycleActioned({
            action: PricingBillingCycleActionedAction.Changed,
            selected: cycle === BillingCycle.Yearly ? Selected.Yearly : Selected.Monthly,
        })

        setBillingCycle(cycle)
    }

    const billingCycleContent = content.data?.billing_cycle[0]
    const optionsToggle = {
        name: 'billing',
        optionLeft: {
            label: billingCycleContent?.billed_monthly_label,
            value: BillingCycle.Monthly,
        },
        optionRight: {
            label: billingCycleContent?.billed_yearly_label,
            value: BillingCycle.Yearly,
        },
        legend: billingCycleContent?.legend,
    }
    const hasOptionsContent = optionsToggle.optionLeft.label && optionsToggle.optionRight.label

    React.useEffect(() => {
        // We don't want to send tracking event on the initial page load
        if (hasMounted && debouncedNumOfUser) {
            pricingSliderActioned({numOfUsers: debouncedNumOfUser})
        } else {
            setHasMounted(true)
        }
        // We want to trigger this hook only when debouncedNumOfUser changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedNumOfUser])

    return (
        <Wrapper data-testid={dataTestId}>
            <OptionsWrapper>
                {hasOptionsContent && (
                    <BillingCycleWrapper>
                        <OptionsToggle
                            {...optionsToggle}
                            value={billingCycle}
                            onChange={handleToggleChange}
                            shouldBreakLabel={true}
                        />

                        {billingCycleContent.description && (
                            <Text $color="brand" $variant="paragraph-base">
                                {billingCycleContent.description}
                            </Text>
                        )}
                    </BillingCycleWrapper>
                )}

                <SliderWrapper>
                    <Slider
                        value={numOfUsers}
                        onChange={setNumOfUsers}
                        min={2}
                        max={500}
                        withNumberInput
                        variant="muted"
                        mode="cubic"
                        label={content.data?.slider_label}
                        tooltipText={content.data?.slider_tooltip_text}
                        layout="row"
                    />
                    {hasRichText(content.data.slider_sub_text.richText) && numOfUsers > 249 && (
                        <RichText
                            $variant="paragraph-base"
                            content={content.data.slider_sub_text.richText}
                        />
                    )}
                </SliderWrapper>
            </OptionsWrapper>

            <PricingV2
                content={content.data}
                mode={hasOptionsContent ? billingCycle : BillingCycle.Yearly}
                rawDemoPath={rawDemoPath}
                numberOfUsers={numOfUsers}
            />
        </Wrapper>
    )
}

const Wrapper = styled.div`
    display: grid;
    padding-top: ${({theme}) => theme.spacings.s40};
    padding-bottom: ${({theme}) => theme.spacings.s40};

    ${mq.min('md')} {
        padding-top: ${({theme}) => theme.spacings.s80};
        padding-bottom: ${({theme}) => theme.spacings.s80};
    }
`

const BillingCycleWrapper = styled.div`
    display: grid;
    row-gap: ${({theme}) => theme.spacings.s10};

    label {
        text-align: left !important;
    }

    fieldset {
        align-items: start;
    }

    ${mq.min('md')} {
        row-gap: ${({theme}) => theme.spacings.s20};
        max-width: 450px;
    }

    ${mq.min('lg')} {
        padding-top: ${({theme}) => theme.spacings.s8};
    }
`

const OptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacings.s40};
    align-items: start;
    position: sticky;
    top: -4px;
    background-color: ${({theme}) => theme.colors.background.muted};
    padding-bottom: ${({theme}) => theme.spacings.s40};
    z-index: ${({theme}) => theme.zIndex.cookieBanner};

    ${mq.min('md')} {
        flex-direction: row;
        justify-content: space-between;
        position: relative;
        gap: 0;
    }
`

const SliderWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacings.s20};

    ${mq.min('md')} {
        flex-basis: 50%;
    }
`

const query = graphql`
    query PricingSection {
        allPrismicPricingTiers {
            nodes {
                lang
                ...Pricing
            }
        }

        allPrismicSecondaryPricingTiers {
            nodes {
                lang
                ...SecondaryPricing
            }
        }
    }
`
